import React, { useState, useEffect,useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useAuth } from '../../../contexts/AuthContext';
import { devportalApi } from '../../../utils/devportalApi';
import Select from '../../../components/ui/Select';

const WebhookModal = ({
  isOpen,
  onClose,
  onSave,
  webhook,
  availableEvents,
  loading,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    events: [],
    secret: "",
    status: "active",
    project_id:"",
    retryPolicy: {
      maxRetries: 3,
      retryDelay: 5000,
      backoffMultiplier: 2,
    },
    headers: {},
  });
  const { user } = useAuth();
  const [errors, setErrors] = useState({});
  const [customHeaders, setCustomHeaders] = useState([{ key: "", value: "" }]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(false);
  const canLoad = useMemo(() => !!user?.id, [user?.id]);
  const [tokens, setTokens] = useState([]);
  // const [loading, setLoading] = useState(false);



  const loadTokens = async () => {
    if (!canLoad) return;
    // setLoading(true);
    try {
      const items = await devportalApi.listAccessTokens();
      // Enrich tokens with project information
      const enrichedTokens = (items || []).map(token => {
        const project = projects.find(p => p.id === token.project_id);
        return {
          ...token,
          project: project ? { id: project.id, name: project.name } : null
        };
      });
      setTokens(enrichedTokens);
    } catch (e) {
      console.error('Failed to load access tokens', e);
      setTokens([]);
      alert(e?.message || 'Failed to load access tokens');
    }
    // setLoading(false);
  };

    useEffect(() => {
      loadTokens();
      const loadProjects = async () => {
        if (!canLoad) return;
        setProjectsLoading(true);
        try {
          const items = await devportalApi.listProjects();
          const normalized = (items || []).map(p => ({ id: p.id, name: p.name || p.title }));
          setProjects(normalized);
        } catch (e) {
          console.error('Failed to load projects', e);
          setProjects([]);
        }
        setProjectsLoading(false);
      };
      loadProjects();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canLoad]);

  useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name || "",
        url: webhook.url || "",
        events: webhook.events || [],
        secret: webhook.secret || "",
        status: webhook.status || "active",
        project_id:webhook.project_id,
        retryPolicy: webhook.retryPolicy || {
          maxRetries: 3,
          retryDelay: 5000,
          backoffMultiplier: 2,
        },
        headers: webhook.headers || {},
      });

      // Convert headers object to array for form handling
      const headerEntries = Object.entries(webhook.headers || {});
      setCustomHeaders(
        headerEntries.length > 0
          ? headerEntries.map(([key, value]) => ({ key, value }))
          : [{ key: "", value: "" }],
      );
    } else {
      // Reset form for new webhook
      setFormData({
        name: "",
        url: "",
        events: [],
        secret: `whsec_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        status: "active",
        project_id:"",
        retryPolicy: {
          maxRetries: 3,
          retryDelay: 5000,
          backoffMultiplier: 2,
        },
        headers: {},
      });
      setCustomHeaders([{ key: "", value: "" }]);
    }
    setErrors({});
    setShowAdvanced(false);
  }, [webhook, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Webhook name is required";
    }

    if (!formData.url.trim()) {
      newErrors.url = "Webhook URL is required";
    } else {
      try {
        const url = new URL(formData.url);
        if (!["http:", "https:"].includes(url.protocol)) {
          newErrors.url = "URL must use HTTP or HTTPS protocol";
        }
      } catch (error) {
        newErrors.url = "Please enter a valid URL";
      }
    }

    if (formData.events.length === 0) {
      newErrors.events = "At least one event must be selected";
    }

    if (
      formData.retryPolicy.maxRetries < 0 ||
      formData.retryPolicy.maxRetries > 10
    ) {
      newErrors.maxRetries = "Max retries must be between 0 and 10";
    }

    if (
      formData.retryPolicy.retryDelay < 1000 ||
      formData.retryPolicy.retryDelay > 300000
    ) {
      newErrors.retryDelay = "Retry delay must be between 1000ms and 300000ms";
    }

    if (
      formData.retryPolicy.backoffMultiplier < 1 ||
      formData.retryPolicy.backoffMultiplier > 5
    ) {
      newErrors.backoffMultiplier =
        "Backoff multiplier must be between 1 and 5";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Convert headers array back to object
    const headers = {};
    customHeaders.forEach(({ key, value }) => {
      if (key.trim() && value.trim()) {
        headers[key.trim()] = value.trim();
      }
    });

    onSave({
      ...formData,
      headers,
    });
  };

  const handleEventToggle = (eventId) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter((e) => e !== eventId)
        : [...prev.events, eventId],
    }));
  };

  const addHeaderField = () => {
    setCustomHeaders((prev) => [...prev, { key: "", value: "" }]);
  };

  const updateHeaderField = (index, field, value) => {
    setCustomHeaders((prev) =>
      prev.map((header, i) =>
        i === index ? { ...header, [field]: value } : header,
      ),
    );
  };

  const removeHeaderField = (index) => {
    if (customHeaders.length > 1) {
      setCustomHeaders((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const generateNewSecret = () => {
    const newSecret = `whsec_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
    setFormData((prev) => ({ ...prev, secret: newSecret }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl h-[90vh] overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
            <h2 className="text-xl font-semibold text-foreground">
              {webhook ? "Edit Webhook" : "Create New Webhook"}
            </h2>
            <Button type="button" variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-8">
              {/* Basic Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Webhook Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-md bg-background text-foreground ${
                      errors.name ? "border-destructive" : "border-border"
                    } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                    placeholder="e.g., Production Message Handler"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>

               <div className="flex gap-3">
                            <Select
                              className="flex-1"
                              label="Project"
                              placeholder={projectsLoading ? 'Loading projects...' : 'Select a project'}
                              options={projects.map(p => ({ value: p.id, label: p.name }))}
                              value={formData.project_id}
                              onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          project_id: e,
                                        }))
                                      }
                              loading={projectsLoading}
                              clearable={!webhook}
                              searchable={!webhook}
                              disabled={!!webhook}
                              />
                </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Endpoint URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      url: e.target.value,
                    }))
                  }
                  className={`w-full px-3 py-2 border rounded-md bg-background text-foreground ${
                    errors.url ? "border-destructive" : "border-border"
                  } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                  placeholder="https://your-domain.com/webhooks/handler"
                />
                {errors.url && (
                  <p className="text-sm text-destructive mt-1">{errors.url}</p>
                )}
              </div>

              {/* Event Selection - Full Width with Better Layout */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground">
                    Events to Subscribe *
                  </h3>
                  <div className="text-sm text-muted-foreground">
                    {formData.events.length} of {availableEvents.length}{" "}
                    selected
                  </div>
                </div>
                {errors.events && (
                  <p className="text-sm text-destructive mb-3">
                    {errors.events}
                  </p>
                )}

                {/* Select All / None buttons */}
                <div className="flex items-center space-x-2 mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        events: availableEvents.map((e) => e.id),
                      }))
                    }
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, events: [] }))
                    }
                  >
                    Clear All
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto border border-border rounded-lg p-4 bg-muted/20">
                  {availableEvents.map((event) => (
                    <label
                      key={event.id}
                      className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.events.includes(event.id)
                          ? "border-primary bg-primary/10 shadow-sm"
                          : "border-border hover:bg-background hover:shadow-sm"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.events.includes(event.id)}
                        onChange={() => handleEventToggle(event.id)}
                        className="mt-1 accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground leading-tight">
                          {event.label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Security Section */}
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">
                  Security
                </h3>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Webhook Secret
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={formData.secret}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          secret: e.target.value,
                        }))
                      }
                      className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono text-sm"
                      placeholder="Webhook secret for signature verification"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={generateNewSecret}
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used to verify webhook payload signatures. Keep this secret
                    secure.
                  </p>
                </div>
              </div>

              {/* Advanced Settings - Better Layout */}
              <div className="border-t border-border pt-6">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h3 className="text-lg font-medium text-foreground">
                    Advanced Configuration
                  </h3>
                  <Icon
                    name={showAdvanced ? "ChevronUp" : "ChevronDown"}
                    size={20}
                    className="text-muted-foreground"
                  />
                </button>

                {showAdvanced && (
                  <div className="mt-6 space-y-6">
                    {/* Retry Policy */}
                    <div className="bg-muted/30 p-6 rounded-lg border border-border">
                      <h4 className="text-sm font-semibold text-foreground mb-4">
                        Retry Policy
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm text-muted-foreground mb-2">
                            Max Retries
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={formData.retryPolicy.maxRetries}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                retryPolicy: {
                                  ...prev.retryPolicy,
                                  maxRetries: parseInt(e.target.value) || 0,
                                },
                              }))
                            }
                            className={`w-full px-3 py-2 border rounded bg-background text-foreground ${
                              errors.maxRetries
                                ? "border-destructive"
                                : "border-border"
                            } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                          />
                          {errors.maxRetries && (
                            <p className="text-xs text-destructive mt-1">
                              {errors.maxRetries}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm text-muted-foreground mb-2">
                            Retry Delay (ms)
                          </label>
                          <input
                            type="number"
                            min="1000"
                            max="300000"
                            value={formData.retryPolicy.retryDelay}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                retryPolicy: {
                                  ...prev.retryPolicy,
                                  retryDelay: parseInt(e.target.value) || 5000,
                                },
                              }))
                            }
                            className={`w-full px-3 py-2 border rounded bg-background text-foreground ${
                              errors.retryDelay
                                ? "border-destructive"
                                : "border-border"
                            } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                          />
                          {errors.retryDelay && (
                            <p className="text-xs text-destructive mt-1">
                              {errors.retryDelay}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm text-muted-foreground mb-2">
                            Backoff Multiplier
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.1"
                            value={formData.retryPolicy.backoffMultiplier}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                retryPolicy: {
                                  ...prev.retryPolicy,
                                  backoffMultiplier:
                                    parseFloat(e.target.value) || 2,
                                },
                              }))
                            }
                            className={`w-full px-3 py-2 border rounded bg-background text-foreground ${
                              errors.backoffMultiplier
                                ? "border-destructive"
                                : "border-border"
                            } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                          />
                          {errors.backoffMultiplier && (
                            <p className="text-xs text-destructive mt-1">
                              {errors.backoffMultiplier}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Custom Headers */}
                    <div className="bg-muted/30 p-6 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-foreground">
                          Custom Headers
                        </h4>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="Plus"
                          iconPosition="left"
                          onClick={addHeaderField}
                        >
                          Add Header
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {customHeaders.map((header, index) => (
                          <div key={index} className="flex space-x-3">
                            <input
                              type="text"
                              placeholder="Header name (e.g., Authorization)"
                              value={header.key}
                              onChange={(e) =>
                                updateHeaderField(index, "key", e.target.value)
                              }
                              className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                            />
                            <input
                              type="text"
                              placeholder="Header value (e.g., Bearer token123)"
                              value={header.value}
                              onChange={(e) =>
                                updateHeaderField(
                                  index,
                                  "value",
                                  e.target.value,
                                )
                              }
                              className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                            />
                            {customHeaders.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeHeaderField(index)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Icon name="Trash2" size={16} />
                              </Button>
                            )}
                          </div>
                        ))}

                        {customHeaders.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No custom headers configured. Click "Add Header" to
                            add one.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={webhook ? "Save" : "Plus"}
              iconPosition="left"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : webhook
                  ? "Update Webhook"
                  : "Create Webhook"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebhookModal;
